import { useLazyQuery } from '@apollo/client'
import { IconButton } from '@material-ui/core'
import ClearAllIcon from '@material-ui/icons/ClearAll'
import DoneAllIcon from '@material-ui/icons/DoneAll'
import Skeleton from '@material-ui/lab/Skeleton'
import { useCallback, useEffect, useMemo } from 'react'

import RatePlanItemSmallBoxForContract from '../../components/RatePlanSmallBoxForContract'
import SectionTitle from '../../components/SectionTitle'
import { RATEPLANS_QUERY } from '../../graphql/rateplan'
import {
  AccommodationDataWithRatePlans,
  RatePlanWithSelectoption
} from '../../types/accommodation'
import { RatePlanBrief } from '../../types/rateplan'
import useStyles from './styles/EntitySelection.styles'

interface EntitySelectionItemsBoxProps {
  accData: AccommodationDataWithRatePlans
  onChangeAccommodationRatePlans: (
    callbackFunc: (
      currentData: RatePlanWithSelectoption[]
    ) => RatePlanWithSelectoption[]
  ) => void
  providerCode: string
}

export const EntitySelectionItemsBox: React.FC<
  EntitySelectionItemsBoxProps
> = ({ accData, onChangeAccommodationRatePlans, providerCode }) => {
  const classes = useStyles()
  const [getRatePlans, { loading }] = useLazyQuery<{
    ratePlans: RatePlanBrief[]
  }>(RATEPLANS_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    onCompleted: (resp) => {
      if (resp.ratePlans.length > 0) {
        onChangeAccommodationRatePlans(() => resp.ratePlans)
      }
    }
  })

  useEffect(() => {
    getRatePlans({
      variables: {
        propertyCode: accData.propertyCode || accData.code || accData.value,
        showDeletedRatePlans: false,
        showDeactivedRatePlans: false,
        providerCode: providerCode
      }
    })
  }, [])

  const handleToggleRatePlan = useCallback((id) => {
    onChangeAccommodationRatePlans((currentData) => {
      const itemIndex = currentData.findIndex((item) => item.id === id)
      return [
        ...currentData.slice(0, itemIndex),
        {
          ...currentData[itemIndex],
          selected: !currentData[itemIndex].selected
        },
        ...currentData.slice(itemIndex + 1)
      ]
    })
  }, [])

  const selectAllRateplan = () => {
    onChangeAccommodationRatePlans((currentData) => {
      return currentData.map((item) => ({
        ...item,
        selected:
          item.selected || accData.ratePlans?.some((rp) => rp.id === item.id)
      }))
    })
  }

  const unSelectAllRateplan = () => {
    onChangeAccommodationRatePlans((currentData) => {
      return currentData.map((item) => ({
        ...item,
        selected: accData.ratePlans?.some((rp) => rp.id === item.id)
          ? false
          : item.selected
      }))
    })
  }

  const allItemsAreSelected = useMemo(
    () =>
      accData.ratePlans &&
      accData.ratePlans?.length > 0 &&
      accData.ratePlans?.every((item) => item.selected),
    [accData]
  )

  return (
    <div className={classes.itemsBox}>
      <div className={classes.listView}>
        <div>
          <div className={classes.accHeader}>
            <SectionTitle title={accData.label} />
            <div className={classes.accHeaderButtons}>
              <IconButton
                onClick={selectAllRateplan}
                color={allItemsAreSelected ? 'primary' : 'default'}>
                <DoneAllIcon />
              </IconButton>
              <IconButton onClick={unSelectAllRateplan}>
                <ClearAllIcon />
              </IconButton>
            </div>
          </div>
          <div className={classes.itemsContainer}>
            {loading
              ? Array(12)
                  .fill('')
                  .map((_, index) => (
                    <div key={index}>
                      <Skeleton
                        className={classes.mainSkeleton}
                        variant="rect"
                        width="100%">
                        <div className={classes.skeletonContainer} />
                      </Skeleton>
                    </div>
                  ))
              : accData?.ratePlans?.map((rateplan, index) => (
                  <RatePlanItemSmallBoxForContract
                    key={index.toString()}
                    ratePlan={rateplan}
                    onToggle={handleToggleRatePlan}
                  />
                ))}
          </div>
        </div>
      </div>
    </div>
  )
}
